'use client'
import { Icon, LoadingScreen } from '@/components'
import { ContainerCustomV2 } from '@/components/common/container-custom-v2'
import { renderOrderStatusChip } from '@/features/account/orders/pages/detail/components'
import { formatDiscountLabel } from '@/features/cart/utils'
import { PopupUpsell } from '@/features/upsell/components/popup-upsell'
import { useUpsellStore } from '@/store/upsell'
import { getVND } from '@/utils/helpers'
import {
  Box,
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { useTranslations } from 'next-intl'
import { useRouter } from 'next/navigation'
import { parseAsInteger, parseAsString, useQueryState } from 'nuqs'
import { useEffect, useState } from 'react'
import { LocalIcon } from '../../components/icon'
import { getOrderInfoByProtectCode, getUpsellOffers } from '../../services'
import { ProductItemInTable } from './components/product-item-in-table'
import { useUnsetActivePopupUpsell } from '@/features/upsell/hooks/use-set-active-popup-upsell'

export const PaymentSuccessContainer = () => {
  const t = useTranslations('Order')
  const router = useRouter()
  const [orderId] = useQueryState('order', parseAsInteger.withDefault(0))
  const [orderCode] = useQueryState('code', parseAsString.withDefault(''))
  const [protected_code] = useQueryState('protect', parseAsString.withDefault(''))
  const [showPopupUpsell, setShowPopupUpsell] = useState(false)
  const { setShowFixedPopupUpsell } = useUpsellStore()
  useUnsetActivePopupUpsell()
  // useEffect(() => {
  //   if (!orderId || !orderCode) {
  //     router.push('/')
  //   }
  // }, [orderCode, orderId, router])

  const { data, isLoading: loadingUpsell } = useQuery({
    queryKey: ['ORDER_OFFER', orderId],
    queryFn: async () => {
      const res = await getUpsellOffers(orderId)
      if (res?.offers.length > 0) {
        setTimeout(() => {
          setShowPopupUpsell(true)
        }, 300)
        return res
      }
    },
    enabled: !!orderId,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  })

  // if (isLoading) return <LoadingScreen />
  // Redirect về home nếu không có protect_code

  // Lấy thông tin đơn hàng dựa vào protect_code
  const { data: orderInfo, isLoading } = useQuery({
    queryKey: ['ORDER_INFO', protected_code],
    queryFn: () => getOrderInfoByProtectCode(protected_code),
    enabled: !!protected_code,
  })

  useEffect(() => {
    if (!orderInfo && !isLoading) {
      router.push('/')
    }
  }, [isLoading, orderInfo, router])
  useEffect(() => {
    if (!protected_code) {
      router.push('/')
    }
  }, [protected_code, router])

  if (isLoading) return <LoadingScreen />
  const shippingFeeRender = (shippingFee: number) => {
    if (shippingFee === 0) {
      return (
        <Box component={'span'} sx={{ color: '#09AD61' }}>
          {t('Miễn phí')}
        </Box>
      )
    }
    return getVND(shippingFee || 0, 'Đ')
  }
  const handleClosePopupUpsell = () => {
    setShowPopupUpsell(false)
    setShowFixedPopupUpsell(true)
  }

  return (
    <>
      {!loadingUpsell && data && (
        <PopupUpsell isOpenPopup={showPopupUpsell} data={data} onClose={handleClosePopupUpsell} />
      )}
      <ContainerCustomV2 component={'main'}>
        <Grid
          container
          spacing={{ xs: 1, md: 2, lg: 3, xl: 4 }}
          sx={{
            paddingY: { xs: 3, md: 7, xl: 10 },
          }}
        >
          <Grid size={{ xs: 12, lg: 6 }}>
            <Stack
              spacing={{
                xs: 1,
                md: 2,
              }}
            >
              {/* Icon */}
              <LocalIcon.Check sx={{ fontSize: { xs: 48, md: 64 }, color: 'primary.main' }} />
              <Stack
                spacing={{
                  xs: 0.5,
                  md: 1,
                }}
              >
                <Typography fontWeight={600} sx={{ fontSize: { xs: '1.5rem', md: '2rem', lg: '2.5rem' } }}>
                  {t('Cảm ơn bạn đã mua hàng')}
                </Typography>

                <Typography variant="body2">Đơn hàng của bạn sẽ được giao đến nơi trong ngày</Typography>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  fontSize={{ xs: '1.25rem', sm: '1.5rem', md: '1.75rem', lg: '2rem' }}
                >
                  {dayjs().add(2, 'day').format('DD') + ' -  ' + dayjs().add(4, 'day').format('DD/MM/YYYY')}.
                </Typography>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <TitleStyle>Thông tin người đặt</TitleStyle>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Tên người đặt')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>{orderInfo?.billing_address?.lastname}</Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Số điện thoại')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                      {orderInfo?.billing_address?.telephone || '-'}
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>Email:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>{orderInfo?.customer_email || '-'}</Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Địa chỉ')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                      {orderInfo?.billing_address?.street}, {orderInfo?.billing_address?.ward},{' '}
                      {orderInfo?.billing_address?.district}, {orderInfo?.billing_address?.city}{' '}
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
              <Divider />
              <Stack spacing={1}>
                <TitleStyle>Thông tin người nhận</TitleStyle>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Tên người nhận')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>{orderInfo?.shipping_address?.lastname}</Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Số điện thoại')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                      {orderInfo?.shipping_address?.telephone || '-'}
                    </Box>
                  </Grid>
                </Grid>
                <Grid container spacing={2} columns={12}>
                  <Grid size={{ xs: 4, md: 3 }}>
                    <Box sx={{ fontWeight: 300, fontSize: 14 }}>{t('Địa chỉ')}:</Box>
                  </Grid>
                  <Grid size={{ xs: 8, md: 9 }}>
                    <Box sx={{ fontWeight: 600, fontSize: 14 }}>
                      {orderInfo?.shipping_address?.street}, {orderInfo?.shipping_address?.ward},{' '}
                      {orderInfo?.shipping_address?.district}, {orderInfo?.shipping_address?.city}{' '}
                    </Box>
                  </Grid>
                </Grid>
              </Stack>
            </Stack>
          </Grid>
          <Grid size={{ xs: 12, lg: 6 }} spacing={2}>
            {orderInfo && (
              <>
                <Stack spacing={3} width={'100%'} alignItems={'center'}>
                  {/* Danh sách sản phẩm */}
                  <Stack spacing={1} width={'100%'}>
                    <Stack
                      direction={'row'}
                      alignItems={'center'}
                      justifyContent={'space-between'}
                      flexWrap={'wrap'}
                      rowGap={1}
                    >
                      <TitleStyle>
                        Sản phẩm{' '}
                        <Typography
                          component={'span'}
                          fontSize={{
                            xs: '0.75rem',
                            md: '0.875rem',
                          }}
                        >
                          ({orderInfo?.products?.reduce((total, item) => total + item.quantity_ordered, 0)}{' '}
                          {'Sản phẩm'})
                        </Typography>
                        <Box component={'span'} ml={1}>
                          {renderOrderStatusChip(orderInfo.status.toLowerCase() as any)}
                        </Box>
                      </TitleStyle>
                      <Stack>
                        <Typography fontSize={{ xs: '0.75rem', md: '0.875rem' }}>
                          Đơn hàng:{' '}
                          <Typography component={'span'} fontWeight={600} fontSize={'inherit'}>
                            #{orderInfo?.number}
                          </Typography>
                        </Typography>
                        <Typography fontSize={{ xs: '0.75rem', md: '0.875rem' }}>
                          {dayjs(orderInfo?.created_at).add(7, 'hours').format('HH:mm:ss DD/MM/YYYY ')}
                        </Typography>
                      </Stack>
                    </Stack>

                    <Box
                      sx={{
                        border: '1px solid',
                        borderColor: 'bd.main',
                        borderRadius: '12px',
                        overflow: 'hidden',
                      }}
                    >
                      {/* MUI Table */}
                      <TableContainer>
                        <Table sx={{ minWidth: { xs: '100%', sm: '400px' } }}>
                          {/* Table Head */}
                          <TableHead sx={{ background: '#F4F5F8' }}>
                            <TableRow></TableRow>
                          </TableHead>

                          {/* Table Body */}
                          <TableBody>
                            {orderInfo?.products?.map((item) => {
                              return <ProductItemInTable key={item.id} product={item} />
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>
                  </Stack>
                  <Stack spacing={1} width={'100%'}>
                    <TitleStyle>Chi tiết thanh toán</TitleStyle>
                    <Stack
                      spacing={{
                        xs: 1,
                        md: 1.5,
                      }}
                    >
                      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="body2">{t('Tạm tính')}:</Typography>
                        <Typography variant="body2">{getVND(orderInfo.total.subtotal.value)}</Typography>
                      </Stack>
                      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="body2">{t('Phí giao hàng')}:</Typography>
                        <Typography variant="body2">
                          {shippingFeeRender(orderInfo.total.total_shipping.value)}
                        </Typography>
                      </Stack>
                      {orderInfo?.total?.discounts?.length > 0 && (
                        <>
                          <Typography variant="body2" fontWeight={600}>
                            {t('Khuyến mãi áp dụng')}:
                          </Typography>
                          {orderInfo?.discount_description?.map((discountItem, index) => (
                            <Stack
                              key={index}
                              direction="row"
                              sx={{ fontSize: { xs: '12px', lg: '14px' } }}
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                            >
                              <Stack
                                direction="row"
                                alignItems={'flex-start'}
                                gap={1}
                                fontSize={'inherit'}
                                flex={1}
                              >
                                <Icon.CheckCircle
                                  color="primary"
                                  sx={{
                                    fontSize: { xs: '0.75rem', md: '1rem' },
                                    mt: 0.3,
                                  }}
                                />
                                <Typography width={'100%'} sx={{ fontSize: 'inherit', fontWeight: 300 }}>
                                  {formatDiscountLabel(discountItem)}
                                </Typography>
                              </Stack>
                              <Typography sx={{ fontSize: 'inherit', fontWeight: 300 }}>
                                -{getVND(discountItem?.amount?.value, 'Đ')}
                              </Typography>
                            </Stack>
                          ))}
                        </>
                      )}
                      <Divider sx={{ my: 1 }} />

                      <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
                        <Typography variant="body1" fontWeight={600}>
                          Tổng tiền
                        </Typography>
                        <Typography variant="body1" fontWeight={600}>
                          {getVND(orderInfo.total.grand_total.value)}
                        </Typography>
                      </Stack>
                    </Stack>
                  </Stack>
                </Stack>
              </>
            )}
          </Grid>
        </Grid>
      </ContainerCustomV2>
    </>
  )
}
const TitleStyle = styled(Typography)(({ theme }) => ({
  fontSize: '1.15rem',
  fontWeight: 600,
  [theme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.5rem',
  },
}))
